/** 地図画面コンポーネント(Report service用) */
<template>
  <b-container id="the-Member">
    <b-row class="container-member">
      <b-col cols="4" class="view-member" v-show="isListViewShown">
        <b-row no-gutters class="tab-bar">
          <div class="tab-container">
            <div
              v-if="isIndoorAvailable"
              class="tab active"
              v-t="'theMember.indoorSiteBuilding'"
            />
          </div>
        </b-row>
        <b-row no-gutters class="tab-content">
          <!-- サイト・ビル -->
          <template v-if="isIndoorAvailable">
            <member-indoor-item
              show
              v-bind:indoor-places="indoorPlaces"
              v-bind:show-users-count="false"
              v-bind:condition="condition"
              v-on:condition-changed="updateCondition"
              v-on:target-ids-changed="updateTargetIdsToRender"
              v-on:selected-id-changed="targetIdToFit = $event"
              v-on:indoor-site-name-click="$_showIndoorSiteDetail"
              v-on:indoor-building-name-click="$_showIndoorBuildingDetail"
            />
          </template>
        </b-row>
      </b-col>
      <b-col v-bind:cols="viewCols.right" class="view-map">
        <!-- 地図 -->
        <map-control
          ref="map-control"
          id="member-map-control"
          v-bind="$store.getters.mapControlBinds"
          v-bind:id-prefix="'member-'"
          v-bind:reactive-render="!isModalOverlaid"
          v-bind:layer-renderers="renderers"
          v-bind:onIfieldApiError="handleApiError"
          v-on:indoor-level-selected="setActiveIndoorLevelId"
          v-on:map-control-initend="$_onMapInitEnd"
          v-on:menu-layers-visible-changed="
            $store.dispatch('map/setVisible', $event)
          "
        >
          <template v-slot:default="{ currentMapComponent, clientRect }">
            <!-- 屋内建物の Popup -->
            <map-popup-indoor-building
              v-bind:current-map-component="currentMapComponent"
              v-bind:map-client-rect="clientRect"
              v-on:map-popup-building-detail-click="$_showIndoorBuildingDetail"
            />
          </template>
          <template v-slot:map-left-top>
            <!-- ビル表示ボタン -->
            <map-indoor-building-button
              v-bind:indoor-building="
                $store.getters['mapIndoor/activeIndoorBuilding']
              "
              class="map-control-ui"
              v-on:reset-click="unselectedBuilding"
            />
            <!-- リスト切り替えタブ -->
            <list-view-toggle-tab
              v-model="isListViewShown"
              v-bind:listViewCols="viewCols.left"
              v-bind:idPrefix="'member'"
              class="map-control-ui list-tab"
              v-on:listview-toggletab-toggled="$_toggleListViewShown"
            />
          </template>
        </map-control>
      </b-col>
    </b-row>

    <place-detail
      ref="place-detail"
      v-on:place-searchreport-click="$_showReport"
      v-on:hide-modal="$_onHidePlaceDetail"
    />
    <indoor-site-detail ref="indoor-site-detail" />
    <indoor-building-detail
      ref="indoor-building-detail"
      v-on:indoor-building-fit-map="fitToBounds"
    />
    <map-layer-controller
      v-bind:layer-render-parameters="mapLayerRenderParameters"
      v-on:map-layer-renderers-created="mapLayerRenderers = $event"
    />
    <!-- モーダル重畳検知 -->
    <modal-overlay-detector
      v-on:is-modal-overlaid-changed="$_onModalOverlayChanged"
    />
  </b-container>
</template>

<script>
import MapPopupIndoorBuilding from '@/components/indoor/AppMapPopupIndoorBuilding.vue'
import MemberIndoorItem from '@/components/member/AppMemberIndoorItem.vue'
import ListViewToggleTab from '@/components/AppListViewToggleTab.vue'
import PlaceDetail from '@/views/ThePlaceDetail.vue'
import IndoorSiteDetail from '@/views/TheIndoorSiteDetail.vue'
import IndoorBuildingDetail from '@/views/TheIndoorBuildingDetail.vue'
import MapLayerController from '@/components/map/AppMapLayerController.vue'
import BvModalOverlayDetector from '@/components/AppBvModalOverlayDetector.vue'
import { MapIndoorBuildingButton } from 'ifield-core-components'
import { RestApiClient } from 'ifield-core-components'
import { shallowRef } from 'vue'
import { mapGetters } from 'vuex'
import { FUNCTION_TYPE } from '@/constants.js'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'
import { useMapLayerFilter } from '@/composables/map/UseMapLayerFilter'
import { useMapIndoorStore } from '@/composables/map/UseMapIndoorStore'
import { useFitFeaturesOnMemberMap } from '@/composables/member/UseFitFeaturesOnMemberMap'
import { useUserStatusFilterCondition } from '@/composables/member/UseUserStatusFilterCondition'
import ModalUtil from '@/js/util/ModalUtil.js'

/**
 * メンバー(iField Report用)
 */
export default {
  name: 'TheMemberForReport',
  components: {
    'member-indoor-item': MemberIndoorItem,
    'map-indoor-building-button': MapIndoorBuildingButton,
    'list-view-toggle-tab': ListViewToggleTab,
    'place-detail': PlaceDetail,
    'indoor-site-detail': IndoorSiteDetail,
    'indoor-building-detail': IndoorBuildingDetail,
    'map-layer-controller': MapLayerController,
    'modal-overlay-detector': BvModalOverlayDetector,
    'map-popup-indoor-building': MapPopupIndoorBuilding,
  },
  setup() {
    /**
     * AppMapControlのインスタンス
     */
    const map = shallowRef(null)
    /**
     * 屋内プレイス
     */
    const indoorPlaces = shallowRef([])

    // 共通使用
    const { handleApiError } = useHandleApiError()

    // 地図表示対象IDフィルタリング
    const {
      targetIds: targetIdsToRender,
      updateTargetIds: updateTargetIdsToRender,
      layerRenderParameters,
    } = useMapLayerFilter()
    // ビル・フロア変更処理
    const { changeIndoorBuilding, unselectedBuilding, setActiveIndoorLevelId } =
      useMapIndoorStore()

    // 絞り込み条件(地図同期用)
    const { condition, updateCondition, applyMapIndoorState } =
      useUserStatusFilterCondition()

    // 地図fit
    const { targetId: targetIdToFit, fitToBounds } = useFitFeaturesOnMemberMap({
      map,
      indoorPlaces,
      condition,
      users: shallowRef([]),
    })

    return {
      map,
      indoorPlaces,
      handleApiError,
      targetIdsToRender,
      updateTargetIdsToRender,
      mapLayerRenderParameters: layerRenderParameters,
      changeIndoorBuilding,
      unselectedBuilding,
      setActiveIndoorLevelId,
      targetIdToFit,
      fitToBounds,
      condition,
      updateCondition,
      applyMapIndoorStateToCondition: applyMapIndoorState,
    }
  },
  data: function () {
    return {
      /**
       * ページが表示されている場合はtrue
       * keep-alive で保持されるため、非表示でも破棄されない
       * @type {Boolean}
       */
      isViewActive: true,
      /**
       * リストビューを表示する場合true
       * @type {Boolean}
       */
      isListViewShown: true,
      /**
       * ロード中の場合はtrue
       * @type {Boolean}
       */
      isLoading: false,
      /**
       * モーダルが重畳している場合はtrue
       * @type {Boolean}
       */
      isModalOverlaid: false,
      /**
       * マップレイヤーのレンダラー
       * @type {Array.<Object>}
       */
      mapLayerRenderers: [],
    }
  },
  computed: {
    ...mapGetters('mapLayer', ['clickedPlace']),
    ...mapGetters('mapIndoor', [
      'activeIndoorLevelId',
      'activeIndoorBuildingId',
    ]),
    ...mapGetters('indoorSite', ['indoorSiteMap']),
    ...mapGetters('indoorBuilding', ['indoorBuildingMap']),
    ...mapGetters('indoorLevel', ['indoorLevels']),
    /**
     * 屋内機能が利用可能か？
     * @type {Boolean}
     */
    isIndoorAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.INDOOR)
    },
    /**
     * 地図binding用render関数配列
     * @type {Array.<Function>}
     */
    renderers: function () {
      return this.mapLayerRenderers.map((r) => r.render)
    },
    /**
     * 全ての屋内レベルID
     * @type {Array.<Number>}
     */
    allIndoorLevelIds: function () {
      return this.indoorLevels.map((level) => level.id)
    },
    /**
     * 屋内アクティブにすべきビルID
     * condition.indoorで以下の条件を満たす場合に限り値が入る
     * * ビルが1つだけ選択されている
     * * フロアが選択されている
     * @type {Number}
     */
    indoorBuildingIdForActive: function () {
      const {
        indoor: { indoorBuildingIds, indoorLevelId },
      } = this.condition
      if (
        !indoorBuildingIds ||
        indoorBuildingIds.length !== 1 ||
        !indoorLevelId
      )
        return null
      return indoorBuildingIds[0]
    },
    /**
     * ビュー分割のcol数
     */
    viewCols: function () {
      return this.isListViewShown
        ? { left: 4, right: 8 }
        : { left: 0, right: 12 }
    },
  },
  watch: {
    // 絞り込み条件設定で屋内に出入りが必要なときの処理
    indoorBuildingIdForActive: function (value) {
      if (value) {
        // 地図側の屋内建物更新
        const building = this.indoorBuildingMap.get(value)
        building && this.changeIndoorBuilding(building)
      } else {
        this.changeIndoorBuilding(null)
      }
    },
    // 絞り込み条件設定でフロアだけが変更されたときの処理
    'condition.indoor.indoorLevelId': function (value) {
      if (value) {
        this.setActiveIndoorLevelId(value)
        if (this.indoorBuildingIdForActive) {
          const building = this.indoorBuildingMap.get(
            this.indoorBuildingIdForActive
          )
          building && this.changeIndoorBuilding(building)
        }
      }
    },
    // 地図側で屋内に出入りしたときの処理
    activeIndoorLevelId: function (value) {
      if (value) {
        this.applyMapIndoorStateToCondition()
      }
    },
    // 地図側で屋内に出入りしたときの処理
    activeIndoorBuildingId: function (value) {
      if (!value) {
        // サイト・ビルタブの表示対象がそのまま condition に復元されるようにする
        this.updateCondition({
          indoor: {
            indoorSiteIds: this.targetIdsToRender.indoorSite,
            indoorBuildingIds: this.targetIdsToRender.indoorBuilding,
            indoorLevelId: null,
          },
          place: {
            placeId: null,
          },
        })
      }
    },
    clickedPlace(value) {
      // Click された Feature が変更された
      // プレイス詳細ダイアログを表示する
      this.$log.debug('[clickedPlace] ', value)
      // 保持されている非表示のページでも watcher は動くので、表示中のページだけが開く
      if (value && this.isViewActive && !this.isModalOverlaid) {
        this.$_showPlaceDialog(value.properties.place_id)
      }
    },
  },
  created: function () {
    this.$_updateIndoorSite()
    this.$_updateIndoorBuilding()
    this.$_updateIndoorLevel().then(() => {
      this.allIndoorLevelIds.length && this.$_getIndoorPlaces()
    })
  },
  mounted: function () {
    this.$log.debug('[DEBUG] TheMemberForReport mounted')

    // 建物のピンをクリック時にアクティブにさせないようにする
    this.$store.commit('mapIndoor/setClickBuildingPinToActive', false)
  },
  beforeDestroy: function () {
    this.$log.debug('[DEBUG] TheMemberForReport beforeDestroy')
    // 建物のピンをクリック時にアクティブにする (元に戻す)
    this.$store.commit('mapIndoor/setClickBuildingPinToActive', true)
  },
  activated: function () {
    // 初回描画時は created / mounted で初期化済みなので何もしない
    if (this.isViewActive) return
    this.$log.debug('[DEBUG] TheMemberForReport activated')
    this.isViewActive = true
    // 建物のピンをクリック時にアクティブにさせないようにする
    this.$store.commit('mapIndoor/setClickBuildingPinToActive', false)
  },
  deactivated: function () {
    this.$log.debug('[DEBUG] TheMemberForReport deactivated')
    this.isViewActive = false
    // 建物のピンをクリック時にアクティブにする (元に戻す)
    this.$store.commit('mapIndoor/setClickBuildingPinToActive', true)
    // 開いたままのダイアログを閉じる
    ModalUtil.hideAllIn(this)
  },
  methods: {
    /**
     * 屋内サイト情報更新
     */
    $_updateIndoorSite: async function () {
      try {
        await this.$store.dispatch('indoorSite/updateIndoorSitesWithThrottle')
      } catch (error) {
        this.handleApiError(error, () => {
          this.$log.warn('failed to fetch indoorSites. use fetched before.')
        })
      }
    },

    /**
     * 屋内建物情報更新
     */
    $_updateIndoorBuilding: async function () {
      try {
        await this.$store.dispatch(
          'indoorBuilding/updateIndoorBuildingsWithThrottle'
        )
      } catch (error) {
        this.handleApiError(error, () => {
          this.$log.warn('failed to fetch indoorBuildings. use fetched before.')
        })
      }
    },

    /**
     * 屋内レベル情報更新
     */
    $_updateIndoorLevel: async function () {
      try {
        await this.$store.dispatch('indoorLevel/updateIndoorLevelsWithThrottle')
      } catch (error) {
        this.handleApiError(error, () => {
          this.$log.warn('failed to fetch indoorLevels. use fetched before.')
        })
      }
    },

    /**
     * プレイスを取得する
     */
    $_getIndoorPlaces: async function () {
      const url = RestApiClient.createApiUrl('place')
      url.searchParams.append(
        'indoor_level_id',
        this.allIndoorLevelIds.join(',')
      )
      try {
        const json = await RestApiClient.executeGetApi(url)
        this.indoorPlaces = json.data
      } catch (error) {
        this.handleApiError(error, () => {
          this.$log.error('Failed to fetch indoor places')
        })
      }
    },

    /**
     * プレイス詳細ダイアログを表示する
     * @param {Number} recordId 表示対象のプレイスレコードのID
     */
    $_showPlaceDialog: function (recordId) {
      // ダイアログを表示する
      this.$refs['place-detail'].showModal(recordId)
    },

    /**
     * 報告書ページへ遷移する
     * @param {Array.<Number>} placeIds プレイスID配列
     */
    $_showReport: function (placeIds) {
      this.$router.push({
        name: 'report',
        params: {
          defaultPlaceIds: placeIds,
        },
      })
    },

    /**
     * プレイス詳細非表示時処理
     * @param {Boolean} isUpdated 表示されていたプレイスが更新された場合はtrue
     */
    $_onHidePlaceDetail: function (isUpdated) {
      if (isUpdated) {
        // 地図の強制再描画
        const deck = this.map.getMapDeckController()
        deck && deck.render(true)
        // プレイスタブの再更新
        this.$refs['member-place'].refresh()
        // ユーザーリスト更新
        this.$_updateUsers(false)
      }
    },

    /**
     * ベースマップ初期化完了時処理
     */
    $_onMapInitEnd: function () {
      this.map = this.$refs['map-control']
    },

    /**
     * 現場詳細表示
     * @param {Number} id 現場ID
     */
    $_showIndoorSiteDetail: function (id) {
      this.$refs['indoor-site-detail'].showModal(id)
    },

    /**
     * 建物詳細表示
     * @param {Number} id 現場ID
     */
    $_showIndoorBuildingDetail: function (id) {
      this.$refs['indoor-building-detail'].showModal(id)
    },

    /**
     * リストビューの表示・非表示を切り替える
     */
    $_toggleListViewShown: function () {
      this.isListViewShown = !this.isListViewShown
    },

    /**
     * モーダル重畳状態変更時処理
     * @param {Boolean} value モーダル重畳状態
     */
    $_onModalOverlayChanged: function (value) {
      this.isModalOverlaid = value
    },

    /**
     * ローディングを表示する
     */
    $_showLoading: function () {
      this.isLoading = true
    },

    /**
     * ローディングを非表示にする
     */
    $_hideLoading: function () {
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../assets/scss/parts/container';
@use '../assets/scss/parts/tool-bar';
@use '../assets/scss/parts/title-bar';

$-tab-height: 40px;

.container-member {
  /*  display: -webkit-box !important; */
  display: flexbox !important;
  margin: 0 !important;
  height: 100%;
}

.view-member {
  width: 100%;
  height: 100%;
  padding: 8px 10px 0 0 !important;
  box-sizing: border-box;
  background-color: #ffffff;
  transition: 0.3s;
}

.view-map {
  display: -webkit-box !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
  overflow: hidden !important;
}

.tab-bar {
  height: $-tab-height;
  display: grid;
  grid-template-columns: 1fr 150px;
  justify-content: space-between;
  align-items: center;
}

.tab-container {
  height: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 1px;
}

.tab {
  width: auto;
  height: $-tab-height;
  margin: 0 0 -1px 0;
  padding: 0 clamp(8px, 4%, 20px);
  background-color: #e9ecef;
  border: 1px solid #cfcfcf;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  font-weight: $font-weight-bold;
  font-size: $font-size-middle;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  user-select: none;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    background-color: #e2e6ea;
  }

  &.active {
    background-color: #ffffff;
    border-bottom-color: #ffffff;
  }
}

.tab-content {
  height: calc(100% - #{$-tab-height});
  border: 1px solid #cfcfcf;
  border-top-right-radius: 8px;
}

.list-tab {
  position: absolute;
  left: 0;
  top: 16px;
}

#member-map-control {
  ::v-deep .control-wrapper {
    &.left-top-wrapper,
    &.left-bottom-wrapper {
      padding-left: 40px;
    }

    .map-control-ui {
      pointer-events: auto;
    }
  }
}
</style>
